import axios from 'axios';
import { toast } from 'react-toastify';

const BASE_URL = '/api';
const AUTH_FAILURE_STATUSES = new Set([401, 403]);
let isRecoveringFromAuthFailure = false;


const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

type MutationMethod = 'post' | 'put' | 'delete';
type RequestConfigLike = {
  method?: string;
  url?: string;
};
type ErrorLike = {
  config?: RequestConfigLike;
  response?: {
    status?: number;
    data?: unknown;
  };
  request?: unknown;
  message?: string;
  isAxiosError?: boolean;
};

const MUTATION_METHODS: MutationMethod[] = ['post', 'put', 'delete'];

function isMutationRequest(config?: RequestConfigLike): config is RequestConfigLike {
  const method = config?.method?.toLowerCase();
  return Boolean(method && MUTATION_METHODS.includes(method as MutationMethod));
}

function getResponseMessage(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === 'string') return data;

  if (typeof data === 'object') {
    const payload = data as Record<string, unknown>;
    const possibleMessage = payload.message ?? payload.msg ?? payload.error;
    if (typeof possibleMessage === 'string' && possibleMessage.trim()) {
      return possibleMessage;
    }
  }

  return null;
}

function getSuccessMessage(config?: RequestConfigLike, data?: unknown): string {
  const apiMessage = getResponseMessage(data);
  if (apiMessage) return apiMessage;

  switch (config?.method?.toLowerCase()) {
    case 'post':
      return 'Created successfully.';
    case 'put':
      return 'Updated successfully.';
    case 'delete':
      return 'Deleted successfully.';
    default:
      return 'Request completed successfully.';
  }
}

function shouldRecoverAuth(error: ErrorLike): boolean {
  const status = error.response?.status;
  const requestUrl = error.config?.url || '';

  if (!status || !AUTH_FAILURE_STATUSES.has(status)) {
    return false;
  }

  if (
    requestUrl.includes('/auth/login') ||
    requestUrl.includes('/auth/logout') ||
    requestUrl.includes('/auth/signup')
  ) {
    return false;
  }

  return true;
}

function recoverFromAuthFailure(): void {
  if (typeof window === 'undefined' || isRecoveringFromAuthFailure) {
    return;
  }

  isRecoveringFromAuthFailure = true;

  void fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
  }).finally(() => {
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
      return;
    }

    isRecoveringFromAuthFailure = false;
  });
}

export function handleApiError(error: unknown): string {
  const axiosError = error as ErrorLike;
  const apiMessage = getResponseMessage(axiosError?.response?.data);
  if (apiMessage) {
    return apiMessage;
  }

  if (axiosError.response) {
    switch (axiosError.response.status) {
      case 400:
        return 'Bad Request. Please check your input.';
      case 401:
        return 'Session expired. Please log in again.';
      case 403:
        return 'Your session is no longer valid or you do not have access.';
      case 404:
        return 'Resource not found.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return 'Unexpected error occurred.';
    }
  } else if (axiosError.request) {
    return 'No response from server. Please check your connection.';
  } else {
    return 'Request error: ' + (axiosError.message || 'Unknown error');
  }
}

api.interceptors.response.use(
  (response) => {
    if (typeof window !== 'undefined' && isMutationRequest(response.config)) {
      toast.success(getSuccessMessage(response.config, response.data));
    }

    return response;
  },
  (error: ErrorLike) => {
    const authFailure = shouldRecoverAuth(error);

    if (!authFailure) {
      console.error('API Error:', error);
    } else {
      recoverFromAuthFailure();
    }

    if (typeof window !== 'undefined' && isMutationRequest(error.config)) {
      toast.error(handleApiError(error));
    }

    return Promise.reject(error);
  }
);

export default api;
